import { Text } from "../../../../components/common/typography";
import type { CartEarn } from "../../utils/calculate-cart-earn";

interface LoyaltyEarnSummaryProps {
  earn: CartEarn;
}

/**
 * "You'll earn" block for checkout. When promotions give cart lines different
 * multipliers, it lists points per product (with the active multiplier) instead
 * of a single, confusing blended factor. With a uniform rate it stays compact.
 */
export function LoyaltyEarnSummary({ earn }: LoyaltyEarnSummaryProps) {
  if (earn.total <= 0) return null;

  const fmtMult = (m: number) => `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}×`;

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-accentGreenTintBg px-3 py-2">
      <div className="flex items-center justify-between">
        <Text variant="bodySm" tone="dark">You'll earn</Text>
        <Text variant="bodySmBold" tone="dark" inline>
          {earn.total.toLocaleString()} points
        </Text>
      </div>

      {earn.hasBonus &&
        (earn.isMixed ? (
          <div className="flex flex-col gap-1 border-t border-accentGreenTint pt-2">
            {earn.lines.map((line) => (
              <div key={line.id} className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <Text variant="bodyXs" tone="body" className="truncate">{line.name}</Text>
                  {line.multiplier > 1.0001 && (
                    <Text variant="bodyXsBold" tone="accent" inline>{fmtMult(line.multiplier)}</Text>
                  )}
                </div>
                <Text variant="bodyXs" tone="body" inline>
                  {line.points.toLocaleString()} pts
                </Text>
              </div>
            ))}
          </div>
        ) : (
          <Text variant="bodyXsBold" tone="accent">
            {fmtMult(earn.lines[0]?.multiplier ?? 1)} bonus points applied
          </Text>
        ))}
    </div>
  );
}
